import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { localizeAsset } from "@/lib/assets";
import { Loader } from "@/components/AppShell";
import { ArrowLeft, Flame } from "lucide-react";

const NARRATIVE = `Ianas nasce nel luglio 2024 come immagine di un incontro interiore. Tre figure femminili danzano intorno a un fuoco sospeso: non un fuoco che consuma, ma una presenza che illumina e trasforma.

Il viola chiaro richiama la dimensione spirituale, mentre la materia in rilievo rende il Fuoco dello Spirito una presenza viva. Il giallo introduce chiarezza, consapevolezza e apertura.

La danza racconta la possibilità di attraversare il cambiamento senza perdere la gioia e la leggerezza. Le tre figure non indicano una verità da accettare, ma invitano ciascuno a riconoscere ciò che dentro di sé desidera tornare a muoversi, creare e risplendere.

Da questa stessa visione nasce Ascolta Anima: uno spazio quotidiano in cui parole, simboli e piccoli gesti aiutano a incontrare con maggiore consapevolezza ciò che sta emergendo dentro di noi.`;

const NOTE = "L'opera e la sua lettura simbolica appartengono al percorso artistico e spirituale di Anna Checchi. Ogni persona è libera di incontrarla attraverso la propria sensibilità.";

export function ProtectedArt({ src, alt, className }) {
  return (
    <img
      src={src} alt={alt} className={className} draggable={false}
      onContextMenu={(e) => e.preventDefault()}
      style={{ userSelect: "none", WebkitUserSelect: "none", pointerEvents: "none" }}
    />
  );
}

export default function IanasStory() {
  const [art, setArt] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { api.get("/artwork").then((r) => setArt(r.data)).catch(() => setArt({ page_enabled: false })); }, []);
  if (!art) return <Loader />;

  if (art.page_enabled === false) {
    return (
      <div className="anima-backdrop min-h-screen flex items-center justify-center px-7 text-center">
        <div>
          <p className="font-serif text-2xl text-anima-ink mb-4">Questa pagina non è al momento disponibile.</p>
          <button onClick={() => navigate(-1)} className="rounded-full bg-anima-sagedeep text-anima-ivory px-6 py-3">Indietro</button>
        </div>
      </div>
    );
  }

  const narrative = art.narrative || NARRATIVE;

  return (
    <div className="anima-backdrop min-h-screen w-full flex justify-center">
      <div className="w-full max-w-md px-7 py-10">
        <button onClick={() => navigate(-1)} className="text-anima-muted mb-6 flex items-center gap-1 text-sm" data-testid="ianas-back">
          <ArrowLeft size={16} /> Indietro
        </button>

        <div className="flex items-center gap-2 mb-2 text-anima-golddeep">
          <Flame size={16} />
          <span className="text-[11px] uppercase tracking-widest">La storia dell'opera</span>
        </div>

        <motion.figure
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}
          className="rounded-3xl overflow-hidden bg-white/50 border border-anima-ink/8 mb-5"
        >
          <ProtectedArt src={localizeAsset(art.image_url)} alt={art.alt_text} className="w-full h-auto block" />
          <figcaption className="px-4 py-3 text-[12px] text-anima-muted">
            <span className="font-serif text-anima-ink text-base">{art.title}</span> — {art.author}
          </figcaption>
        </motion.figure>

        <h1 className="font-serif text-4xl text-anima-ink leading-tight">{art.title}</h1>
        <button
          onClick={() => navigate("/chi-sono")}
          className="text-anima-muted mt-1 mb-5 underline decoration-anima-gold/60 underline-offset-2"
          data-testid="ianas-author-link"
        >
          {art.author}
        </button>

        <div className="grid grid-cols-3 gap-2 mb-7">
          {[["Tecnica", art.technique], ["Dimensioni", art.size], ["Anno", art.year]].map(([l, v]) => (
            <div key={l} className="rounded-2xl bg-white/50 border border-anima-ink/5 px-3 py-3 text-center">
              <p className="text-[10px] uppercase tracking-wider text-anima-muted">{l}</p>
              <p className="text-[13px] text-anima-ink mt-0.5">{v}</p>
            </div>
          ))}
        </div>

        <div className="space-y-4 text-[15px] text-anima-ink/90 leading-relaxed whitespace-pre-line mb-8">
          {narrative}
        </div>

        <div className="rounded-3xl bg-anima-purple/30 border border-anima-ink/5 p-5 mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Flame size={16} className="text-anima-golddeep" />
            <p className="font-serif text-xl text-anima-ink">Il Fuoco dello Spirito</p>
          </div>
          <p className="text-sm text-anima-ink/90 leading-relaxed">
            Trasformazione, chiarezza spirituale, connessione interiore e capacità di ritrovare la propria luce.
            La danza delle tre figure comunica unione, gioia, leggerezza, sorellanza e movimento dell'Anima.
          </p>
        </div>

        <p className="text-sm text-anima-muted leading-relaxed italic mb-6">{NOTE}</p>

        <p className="text-[11px] text-anima-muted text-center border-t border-anima-ink/10 pt-5">{art.copyright}</p>
      </div>
    </div>
  );
}
